import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IStaffListItem } from "../../../@types/admin/AdminStaff";
import { TableSorted } from "../../../components/TableSorted";
import {
  STAFF_LIST_QUERY_KEY,
  useStaffList,
} from "../../../hooks/queries/admin/staff";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";

/** A row plus the derived full name the table sorts and searches on. */
type IStaffRow = IStaffListItem & { fullName: string };

const SEARCH_FIELDS: (keyof IStaffRow)[] = [
  "id",
  "fullName",
  "email",
  "hospitalName",
  "wardName",
];

export function AdminStaffList() {
  document.title = "Staff - Admin";

  const navigate = useNavigate();
  const { staff, isLoading, isError } = useStaffList();

  const rows: IStaffRow[] = useMemo(
    () =>
      staff.map((member) => ({
        ...member,
        fullName: [member.firstName, member.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
      })),
    [staff]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: rows, searchFields: SEARCH_FIELDS });

  const columns: ColumnDef<IStaffRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => <span>{row.original.id}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "fullName",
        header: "Name",
        cell: ({ row }) => <span>{row.original.fullName || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => <span>{row.original.email || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created date",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "hospitalName",
        header: "Hospital name",
        cell: ({ row }) => <span>{row.original.hospitalName || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "wardName",
        header: "Ward name",
        cell: ({ row }) => <span>{row.original.wardName || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "lastLogin",
        header: "Last login",
        // TableSorted renders a literal "-" for any empty accessor value and
        // never calls this renderer, so a never-logged-in staff member shows
        // the same dash as every other blank cell.
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastLogin, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "passwordSet",
        header: "Password set",
        cell: ({ row }) => {
          // Retool coloured this tag green/red from passwordSetTagColors.
          const isSet = row.original.passwordSet?.toLowerCase() === "set";
          return (
            <StatusBadge status={isSet ? "active" : "error"}>
              {row.original.passwordSet || "Not set"}
            </StatusBadge>
          );
        },
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Staff</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, email, hospital, or ward..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the staff list. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[STAFF_LIST_QUERY_KEY]}>
          <TableSorted
            columns={columns}
            data={items}
            onRowClick={(member: IStaffRow) =>
              navigate(`/admin/staff/${member.id}`)
            }
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "staff",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
